import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const typeLabels = { kontingent: 'Kontingent', tur: 'Tur/Rejse', arrangement: 'Arrangement' };

export default function ActivityDetail() {
  const { id } = useParams();
  const { apiFetch } = useAuth();
  const [activity, setActivity] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [editingBudgetItem, setEditingBudgetItem] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [memberSearch, setMemberSearch] = useState('');
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('all');
  const [editForm, setEditForm] = useState({});
  const [budgetForm, setBudgetForm] = useState({ description: '', type: 'expense', expected_amount: '', actual_amount: '', note: '' });
  const [error, setError] = useState('');

  const load = () => {
    Promise.all([
      apiFetch(`/api/activities/${id}`),
      apiFetch(`/api/activities/${id}/participants`),
      apiFetch('/api/members'),
      apiFetch(`/api/activities/${id}/budget`)
    ]).then(([a, p, m, b]) => {
      setActivity(a);
      setParticipants(p);
      setAllMembers(m);
      setBudgetItems(b);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, [id]);

  const openEditModal = () => {
    setEditForm({
      name: activity.name,
      description: activity.description || '',
      date: activity.date ? new Date(activity.date).toISOString().slice(0, 10) : '',
      end_date: activity.end_date ? new Date(activity.end_date).toISOString().slice(0, 10) : '',
      amount_per_person: activity.amount_per_person,
      activity_type: activity.activity_type
    });
    setError('');
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiFetch(`/api/activities/${id}`, { method: 'PUT', body: editForm });
      setShowEditModal(false);
      setMessage('Aktivitet opdateret.');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const openBudgetModal = (item = null) => {
    setEditingBudgetItem(item);
    setBudgetForm(item ? {
      description: item.description,
      type: item.type,
      expected_amount: item.expected_amount,
      actual_amount: item.actual_amount !== null ? item.actual_amount : '',
      note: item.note || ''
    } : { description: '', type: 'expense', expected_amount: '', actual_amount: '', note: '' });
    setError('');
    setShowBudgetModal(true);
  };

  const handleBudgetSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBudgetItem) {
        await apiFetch(`/api/activities/${id}/budget/${editingBudgetItem.id}`, { method: 'PUT', body: budgetForm });
      } else {
        await apiFetch(`/api/activities/${id}/budget`, { method: 'POST', body: budgetForm });
      }
      setShowBudgetModal(false);
      setEditingBudgetItem(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteBudgetItem = async (itemId) => {
    if (!window.confirm('Slet denne budgetpost?')) return;
    await apiFetch(`/api/activities/${id}/budget/${itemId}`, { method: 'DELETE' });
    load();
  };

  const addAllMembers = async () => {
    try {
      const res = await apiFetch(`/api/activities/${id}/add-all-members`, {
        method: 'POST',
        body: { amount: activity.amount_per_person }
      });
      setMessage(res.message);
      load();
    } catch (err) { alert(err.message); }
  };

  const addSelectedMembers = async () => {
    if (selectedMembers.size === 0) return;
    try {
      const res = await apiFetch(`/api/activities/${id}/participants`, {
        method: 'POST',
        body: { member_ids: Array.from(selectedMembers), amount: activity.amount_per_person }
      });
      setMessage(res.message);
      setShowAddModal(false);
      setSelectedMembers(new Set());
      setMemberSearch('');
      load();
    } catch (err) { alert(err.message); }
  };

  const removeParticipant = async (memberId) => {
    if (!window.confirm('Fjern deltager fra aktiviteten?')) return;
    await apiFetch(`/api/activities/${id}/participants/${memberId}`, { method: 'DELETE' });
    load();
  };

  const togglePayment = async (memberId, currentlyPaid) => {
    await apiFetch(`/api/activities/${id}/participants/${memberId}/pay`, {
      method: 'PUT',
      body: { has_paid: !currentlyPaid }
    });
    load();
  };

  const togglePartner = async (memberId, currentlyIncluded) => {
    await apiFetch(`/api/activities/${id}/participants/${memberId}/pay`, {
      method: 'PUT',
      body: { partner_included: !currentlyIncluded }
    });
    load();
  };

  const markAllPaid = async () => {
    const unpaid = participants.filter(p => !p.has_paid);
    for (const p of unpaid) {
      await apiFetch(`/api/activities/${id}/participants/${p.member_id}/pay`, {
        method: 'PUT',
        body: { has_paid: true }
      });
    }
    setMessage(`${unpaid.length} deltagere markeret som betalt.`);
    load();
  };

  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const selectAllFiltered = (filteredMembers) => {
    setSelectedMembers(new Set(filteredMembers.map(m => m.id)));
  };

  const fmt = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? '0,00' : n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (!activity) return <div className="loading">Indlæser...</div>;

  const participantIds = new Set(participants.map(p => p.member_id));
  const availableMembers = allMembers.filter(m => !participantIds.has(m.id) && m.is_active);
  const memberFullName = (m) => [m.name, m.last_name].filter(Boolean).join(' ');
  const filteredAvailable = availableMembers.filter(m =>
    memberFullName(m).toLowerCase().includes(memberSearch.toLowerCase())
  );

  const filteredParticipants = participants.filter(p => {
    if (filter === 'paid') return p.has_paid;
    if (filter === 'unpaid') return !p.has_paid;
    return true;
  });

  const paidCount = participants.filter(p => p.has_paid).length;
  const totalPaid = participants.filter(p => p.has_paid).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const totalExpected = participants.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  // Budget calculations
  const budgetIncome = budgetItems.filter(b => b.type === 'income');
  const budgetExpenses = budgetItems.filter(b => b.type === 'expense');
  const totalExpectedIncome = budgetIncome.reduce((s, b) => s + parseFloat(b.expected_amount || 0), 0);
  const totalActualIncome = budgetIncome.reduce((s, b) => s + parseFloat(b.actual_amount || 0), 0);
  const totalExpectedExpense = budgetExpenses.reduce((s, b) => s + parseFloat(b.expected_amount || 0), 0);
  const totalActualExpense = budgetExpenses.reduce((s, b) => s + parseFloat(b.actual_amount || 0), 0);
  const expectedResult = totalExpectedIncome - totalExpectedExpense;
  const actualResult = totalActualIncome - totalActualExpense;
  const hasBudget = budgetItems.length > 0;
  const hasActuals = budgetItems.some(b => b.actual_amount !== null);

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/app/aktiviteter" style={{color: 'var(--gray-500)', textDecoration: 'none', fontSize: '0.85rem'}}>
            &larr; Tilbage til aktiviteter
          </Link>
          <h1>{activity.name}</h1>
        </div>
        <button className="btn btn-secondary" onClick={openEditModal}>Rediger aktivitet</button>
      </div>

      {message && <div className="alert alert-success">{message}</div>}

      {/* Activity info */}
      <div className="card-grid">
        <div className="card">
          <div className="card-header">Type</div>
          <div className="card-value" style={{fontSize: '1.1rem'}}>{typeLabels[activity.activity_type]}</div>
        </div>
        <div className="card">
          <div className="card-header">Dato</div>
          <div className="card-value" style={{fontSize: '1.1rem'}}>
            {activity.date ? new Date(activity.date).toLocaleDateString('da-DK') : '-'}
            {activity.end_date && ` – ${new Date(activity.end_date).toLocaleDateString('da-DK')}`}
          </div>
        </div>
        <div className="card">
          <div className="card-header">Pris/person</div>
          <div className="card-value" style={{fontSize: '1.1rem'}}>{fmt(activity.amount_per_person)} kr.</div>
        </div>
        <div className="card">
          <div className="card-header">Betalt</div>
          <div className="card-value" style={{fontSize: '1.1rem', color: paidCount === participants.length && participants.length > 0 ? 'var(--success)' : 'var(--warning)'}}>
            {paidCount} / {participants.length}
          </div>
        </div>
      </div>

      {/* Description */}
      {activity.description && (
        <div className="card" style={{marginBottom: '1rem'}}>
          <div className="card-header" style={{marginBottom: '0.5rem'}}>Beskrivelse</div>
          <p style={{margin: 0, whiteSpace: 'pre-wrap', lineHeight: '1.6'}}>{activity.description}</p>
        </div>
      )}

      {/* Budget section */}
      <div style={{marginBottom: '1rem'}}>
        <div className="page-header" style={{marginBottom: '0.75rem'}}>
          <h2 style={{margin: 0, fontSize: '1.2rem'}}>Budget</h2>
          <button className="btn btn-primary btn-sm" onClick={() => openBudgetModal()}>+ Ny budgetpost</button>
        </div>

        {!hasBudget ? (
          <div className="card" style={{textAlign: 'center', color: 'var(--gray-500)', padding: '1.5rem'}}>
            Ingen budgetposter endnu. Tilføj forventede indtægter og udgifter.
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Beskrivelse</th>
                  <th>Type</th>
                  <th className="text-right">Forventet</th>
                  <th className="text-right">Faktisk</th>
                  <th className="text-right">Afvigelse</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {budgetIncome.length > 0 && (
                  <>
                    <tr><td colSpan="6" style={{fontWeight: 600, background: 'var(--gray-50)', padding: '0.4rem 0.75rem', fontSize: '0.85rem'}}>Indtægter</td></tr>
                    {budgetIncome.map(item => {
                      const diff = item.actual_amount !== null ? parseFloat(item.actual_amount) - parseFloat(item.expected_amount) : null;
                      return (
                        <tr key={item.id}>
                          <td>
                            {item.description}
                            {item.note && <span style={{display: 'block', fontSize: '0.8rem', color: 'var(--gray-500)'}}>{item.note}</span>}
                          </td>
                          <td><span className="badge badge-active">Indtægt</span></td>
                          <td className="text-right amount">{fmt(item.expected_amount)} kr.</td>
                          <td className="text-right amount">{item.actual_amount !== null ? `${fmt(item.actual_amount)} kr.` : '-'}</td>
                          <td className="text-right amount" style={{color: diff !== null ? (diff >= 0 ? 'var(--success)' : 'var(--danger)') : 'inherit'}}>
                            {diff !== null ? `${diff >= 0 ? '+' : ''}${fmt(diff)} kr.` : '-'}
                          </td>
                          <td className="text-right" style={{whiteSpace: 'nowrap'}}>
                            <button className="btn btn-secondary btn-sm" style={{marginRight: '0.25rem'}} onClick={() => openBudgetModal(item)}>Rediger</button>
                            <button className="btn btn-danger btn-sm" onClick={() => deleteBudgetItem(item.id)}>Slet</button>
                          </td>
                        </tr>
                      );
                    })}
                  </>
                )}
                {budgetExpenses.length > 0 && (
                  <>
                    <tr><td colSpan="6" style={{fontWeight: 600, background: 'var(--gray-50)', padding: '0.4rem 0.75rem', fontSize: '0.85rem'}}>Udgifter</td></tr>
                    {budgetExpenses.map(item => {
                      const diff = item.actual_amount !== null ? parseFloat(item.actual_amount) - parseFloat(item.expected_amount) : null;
                      return (
                        <tr key={item.id}>
                          <td>
                            {item.description}
                            {item.note && <span style={{display: 'block', fontSize: '0.8rem', color: 'var(--gray-500)'}}>{item.note}</span>}
                          </td>
                          <td><span className="badge">Udgift</span></td>
                          <td className="text-right amount">{fmt(item.expected_amount)} kr.</td>
                          <td className="text-right amount">{item.actual_amount !== null ? `${fmt(item.actual_amount)} kr.` : '-'}</td>
                          <td className="text-right amount" style={{color: diff !== null ? (diff <= 0 ? 'var(--success)' : 'var(--danger)') : 'inherit'}}>
                            {diff !== null ? `${diff >= 0 ? '+' : ''}${fmt(diff)} kr.` : '-'}
                          </td>
                          <td className="text-right" style={{whiteSpace: 'nowrap'}}>
                            <button className="btn btn-secondary btn-sm" style={{marginRight: '0.25rem'}} onClick={() => openBudgetModal(item)}>Rediger</button>
                            <button className="btn btn-danger btn-sm" onClick={() => deleteBudgetItem(item.id)}>Slet</button>
                          </td>
                        </tr>
                      );
                    })}
                  </>
                )}
                {/* Totals */}
                <tr style={{fontWeight: 600, borderTop: '2px solid var(--gray-300)'}}>
                  <td>Indtægter i alt</td>
                  <td></td>
                  <td className="text-right amount">{fmt(totalExpectedIncome)} kr.</td>
                  <td className="text-right amount">{hasActuals ? `${fmt(totalActualIncome)} kr.` : '-'}</td>
                  <td></td>
                  <td></td>
                </tr>
                <tr style={{fontWeight: 600}}>
                  <td>Udgifter i alt</td>
                  <td></td>
                  <td className="text-right amount">{fmt(totalExpectedExpense)} kr.</td>
                  <td className="text-right amount">{hasActuals ? `${fmt(totalActualExpense)} kr.` : '-'}</td>
                  <td></td>
                  <td></td>
                </tr>
                <tr style={{fontWeight: 700, background: 'var(--gray-50)'}}>
                  <td>Resultat</td>
                  <td></td>
                  <td className="text-right amount" style={{color: expectedResult >= 0 ? 'var(--success)' : 'var(--danger)'}}>{fmt(expectedResult)} kr.</td>
                  <td className="text-right amount" style={{color: hasActuals ? (actualResult >= 0 ? 'var(--success)' : 'var(--danger)') : 'inherit'}}>
                    {hasActuals ? `${fmt(actualResult)} kr.` : '-'}
                  </td>
                  <td className="text-right amount" style={{color: hasActuals ? (actualResult - expectedResult >= 0 ? 'var(--success)' : 'var(--danger)') : 'inherit'}}>
                    {hasActuals ? `${actualResult - expectedResult >= 0 ? '+' : ''}${fmt(actualResult - expectedResult)} kr.` : '-'}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Participants section */}
      <div>
        <h2 style={{fontSize: '1.2rem', marginBottom: '0.75rem'}}>Deltagere</h2>
        <div className="card" style={{marginBottom: '1rem'}}>
          <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center'}}>
            <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>+ Vælg deltagere</button>
            {activity.activity_type === 'kontingent' && (
              <button className="btn btn-success" onClick={addAllMembers}>Tilføj alle medlemmer</button>
            )}
            {participants.some(p => !p.has_paid) && (
              <button className="btn btn-secondary" onClick={markAllPaid}>Marker alle betalt</button>
            )}
            <div style={{marginLeft: 'auto', display: 'flex', gap: '0.25rem'}}>
              <button className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter('all')}>Alle ({participants.length})</button>
              <button className={`btn btn-sm ${filter === 'unpaid' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter('unpaid')}>Mangler ({participants.length - paidCount})</button>
              <button className={`btn btn-sm ${filter === 'paid' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter('paid')}>Betalt ({paidCount})</button>
            </div>
          </div>
        </div>

        <div className="card" style={{marginBottom: '1rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap'}}>
          <div><span style={{color: 'var(--gray-500)', fontSize: '0.85rem'}}>Indbetalt:</span> <strong>{fmt(totalPaid)} kr.</strong></div>
          <div><span style={{color: 'var(--gray-500)', fontSize: '0.85rem'}}>Forventet:</span> <strong>{fmt(totalExpected)} kr.</strong></div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Navn</th>
                <th>Partner</th>
                <th>Telefon</th>
                <th className="text-right">Beløb</th>
                <th className="text-center">Betalt</th>
                <th>Betalt dato</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredParticipants.length === 0 ? (
                <tr><td colSpan="7" className="text-center">
                  {participants.length === 0 ? 'Ingen deltagere endnu. Tilføj deltagere ovenfor.' : 'Ingen deltagere matcher filteret.'}
                </td></tr>
              ) : filteredParticipants.map(p => {
                const hasPartner = p.partner_name || p.partner_last_name;
                const partnerName = [p.partner_name, p.partner_last_name].filter(Boolean).join(' ');
                return (
                <tr key={p.member_id}>
                  <td style={{fontWeight: 500}}>{[p.member_name, p.member_last_name].filter(Boolean).join(' ')}</td>
                  <td>
                    {hasPartner ? (
                      <label style={{display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', margin: 0}}>
                        <input
                          type="checkbox"
                          checked={!!p.partner_included}
                          onChange={() => togglePartner(p.member_id, p.partner_included)}
                          style={{width: '16px', height: '16px', cursor: 'pointer'}}
                        />
                        <span style={{fontSize: '0.85rem'}}>{partnerName}</span>
                      </label>
                    ) : <span style={{color: 'var(--gray-400)', fontSize: '0.85rem'}}>-</span>}
                  </td>
                  <td>{p.phone || '-'}</td>
                  <td className="text-right amount">{fmt(p.amount)} kr.</td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={!!p.has_paid}
                      onChange={() => togglePayment(p.member_id, p.has_paid)}
                      style={{width: '18px', height: '18px', cursor: 'pointer'}}
                    />
                  </td>
                  <td>{p.paid_date ? new Date(p.paid_date).toLocaleDateString('da-DK') : '-'}</td>
                  <td className="text-right">
                    <button className="btn btn-danger btn-sm" onClick={() => removeParticipant(p.member_id)}>Fjern</button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add participants modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); setSelectedMembers(new Set()); setMemberSearch(''); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth: '600px'}}>
            <h2>Vælg deltagere</h2>
            <div style={{marginBottom: '0.75rem'}}>
              <input className="form-control" placeholder="Søg efter navn..."
                value={memberSearch} onChange={e => setMemberSearch(e.target.value)} autoFocus />
            </div>
            <div style={{marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <span style={{fontSize: '0.85rem', color: 'var(--gray-500)'}}>
                {selectedMembers.size} valgt af {availableMembers.length} tilgængelige
              </span>
              <button className="btn btn-secondary btn-sm" onClick={() => selectAllFiltered(filteredAvailable)}>Vælg alle</button>
            </div>
            <div style={{maxHeight: '400px', overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: '6px'}}>
              {filteredAvailable.length === 0 ? (
                <div style={{padding: '1rem', textAlign: 'center', color: 'var(--gray-500)'}}>
                  {availableMembers.length === 0 ? 'Alle medlemmer er allerede tilføjet.' : 'Ingen matcher søgningen.'}
                </div>
              ) : filteredAvailable.map(m => {
                const partnerName = [m.partner_name, m.partner_last_name].filter(Boolean).join(' ');
                return (
                <label key={m.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.5rem 0.75rem', cursor: 'pointer',
                  borderBottom: '1px solid var(--gray-100)',
                  background: selectedMembers.has(m.id) ? '#eff6ff' : 'transparent'
                }}>
                  <input type="checkbox" checked={selectedMembers.has(m.id)}
                    onChange={() => toggleMemberSelection(m.id)}
                    style={{width: '16px', height: '16px'}} />
                  <span style={{flex: 1}}>
                    <span style={{fontWeight: 500}}>{memberFullName(m)}</span>
                    {partnerName && <span style={{color: 'var(--gray-500)', fontSize: '0.8rem', marginLeft: '0.5rem'}}>+ {partnerName}</span>}
                  </span>
                </label>
                );
              })}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setShowAddModal(false); setSelectedMembers(new Set()); setMemberSearch(''); }}>Annuller</button>
              <button className="btn btn-primary" onClick={addSelectedMembers} disabled={selectedMembers.size === 0}>
                Tilføj {selectedMembers.size} deltager{selectedMembers.size !== 1 ? 'e' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit activity modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Rediger aktivitet</h2>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Type</label>
                <select className="form-control" value={editForm.activity_type}
                  onChange={e => setEditForm({...editForm, activity_type: e.target.value})}>
                  <option value="kontingent">Kontingent</option>
                  <option value="tur">Tur/Rejse</option>
                  <option value="arrangement">Arrangement</option>
                </select>
              </div>
              <div className="form-group">
                <label>Navn</label>
                <input className="form-control" value={editForm.name}
                  onChange={e => setEditForm({...editForm, name: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Beskrivelse</label>
                <textarea className="form-control" value={editForm.description}
                  onChange={e => setEditForm({...editForm, description: e.target.value})}
                  rows={4}
                  placeholder="Beskriv aktiviteten, praktiske detaljer, tidspunkter mv."
                  style={{resize: 'vertical', minHeight: '80px'}} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Startdato</label>
                  <input className="form-control" type="date" value={editForm.date}
                    onChange={e => setEditForm({...editForm, date: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Slutdato (valgfri)</label>
                  <input className="form-control" type="date" value={editForm.end_date}
                    onChange={e => setEditForm({...editForm, end_date: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label>Pris per person (kr.)</label>
                <input className="form-control" type="number" step="0.01" value={editForm.amount_per_person}
                  onChange={e => setEditForm({...editForm, amount_per_person: e.target.value})} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Annuller</button>
                <button type="submit" className="btn btn-primary">Gem ændringer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Budget item modal */}
      {showBudgetModal && (
        <div className="modal-overlay" onClick={() => { setShowBudgetModal(false); setEditingBudgetItem(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingBudgetItem ? 'Rediger budgetpost' : 'Ny budgetpost'}</h2>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleBudgetSubmit}>
              <div className="form-group">
                <label>Type</label>
                <select className="form-control" value={budgetForm.type}
                  onChange={e => setBudgetForm({...budgetForm, type: e.target.value})}>
                  <option value="income">Indtægt</option>
                  <option value="expense">Udgift</option>
                </select>
              </div>
              <div className="form-group">
                <label>Beskrivelse</label>
                <input className="form-control" value={budgetForm.description}
                  onChange={e => setBudgetForm({...budgetForm, description: e.target.value})} required
                  placeholder={budgetForm.type === 'income' ? 'f.eks. Deltagergebyr' : 'f.eks. Transport, forplejning'} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Forventet beløb (kr.)</label>
                  <input className="form-control" type="number" step="0.01" value={budgetForm.expected_amount}
                    onChange={e => setBudgetForm({...budgetForm, expected_amount: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Faktisk beløb (kr.)</label>
                  <input className="form-control" type="number" step="0.01" value={budgetForm.actual_amount}
                    onChange={e => setBudgetForm({...budgetForm, actual_amount: e.target.value})}
                    placeholder="Udfyldes når kendt" />
                </div>
              </div>
              <div className="form-group">
                <label>Note (valgfri)</label>
                <input className="form-control" value={budgetForm.note}
                  onChange={e => setBudgetForm({...budgetForm, note: e.target.value})}
                  placeholder="Evt. bemærkninger" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowBudgetModal(false); setEditingBudgetItem(null); }}>Annuller</button>
                <button type="submit" className="btn btn-primary">{editingBudgetItem ? 'Gem ændringer' : 'Opret'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
